/* state/political-data.js — fetcher + cache för governance + election.
 *
 * data/governance_data.json:
 *   { kod: { parties: [...], kso: 'M' } }
 *   parties = koalitionspartier; kso = kommunstyrelsens ordförandes parti
 *   ('lokalstyret').
 *
 * data/election_data.json:
 *   { kod: { M: 32.8, S: 21.7, SD: 14.9, ... } }
 *   Procent per parti i senaste kommunvalet. Vi tar dominerande partiet
 *   (max procent) som choropleth-färg.
 *
 * Lazy-load: bara om någon politisk KPI aktiveras. Idempotent — flera
 * setActiveKpi('political:...') triggar bara en fetch.
 *
 * Public API:
 *   await loadPoliticalData()            — fetch + cache (idempotent)
 *   getGoverningKso(kod)                 — t.ex. 'S' eller null
 *   getDominantElectionParty(kod)        — t.ex. 'M' eller null
 *   getElectionShares(kod)               — { M: 32.8, ... } eller null
 *   getCoalitionParties(kod)             — Array<abbr> eller []
 */

const GOV_URL  = '/data/governance_data.json?v=1';
const ELEC_URL = '/data/election_data.json?v=1';

let governanceCache = null;
let electionCache   = null;
let loadPromise     = null;

export async function loadPoliticalData() {
    if (governanceCache && electionCache) return;
    if (!loadPromise) {
        loadPromise = Promise.all([
            fetch(GOV_URL).then((r) => r.json()),
            fetch(ELEC_URL).then((r) => r.json()),
        ]).then(([g, e]) => {
            governanceCache = g || {};
            electionCache   = e || {};
        }).catch((err) => {
            // Lämna cachar som null så nästa anrop kan retry:a — men kasta
            // inte felet vidare, choropleth-uppslag returnerar null vid
            // saknad data och kartan färgar 'transparent'.
            console.warn('[political-data] fetch failed:', err);
            governanceCache = governanceCache || {};
            electionCache   = electionCache   || {};
        });
    }
    return loadPromise;
}

export function getGoverningKso(kod) {
    if (!governanceCache) return null;
    const e = governanceCache[kod];
    return (e && e.kso) || null;
}

export function getCoalitionParties(kod) {
    if (!governanceCache) return [];
    const e = governanceCache[kod];
    return (e && Array.isArray(e.parties)) ? e.parties : [];
}

export function getElectionShares(kod) {
    if (!electionCache) return null;
    return electionCache[kod] || null;
}

export function getDominantElectionParty(kod) {
    const shares = getElectionShares(kod);
    if (!shares) return null;
    let best = null;
    let bestVal = -Infinity;
    for (const [party, pct] of Object.entries(shares)) {
        if (typeof pct === 'number' && pct > bestVal) {
            bestVal = pct;
            best = party;
        }
    }
    return best;
}

export function isPoliticalLoaded() {
    return governanceCache !== null && electionCache !== null;
}
